<template>
    <asli-date
        :id="buildId('claim_date')"
        :name="buildId('claim_date')"
        :rules="computedDateRules"
        label="Date of claim"
        v-model="data.date"
        :showToday="true"
        :dateTo="today"
        :dateFrom="dateFrom"
        :key="claimDateKey"
        calendar-aria-label="Open calendar to select date of claim"
    />
    <asli-dropdown
        :id="buildId('type_of_claim')"
        :name="buildId('type_of_claim')"
        v-model="data.type"
        label="What was the claim for?"
        :options="lists?.claim_type"
        placeholder="Please select an option"
        rules="required-claim-type"
        large
        :tooltipText="YOUR_COVER_HELP_TEXT.claim_type"
    />
    <asli-radio
        :id="buildId('claim_is_settled')"
        :name="buildId('claim_is_settled')"
        v-model="data.is_settled"
        label="Is the claim settled?"
        :options="lists?.yes_no"
        :is-yes-no-option="true"
        rules="required:claim_is_settled"
        return-boolean
    />
    <asli-text-input
        v-if="data.is_settled"
        :id="buildId('claim_amount')"
        :name="buildId('claim_amount')"
        label="What was the settlement amount?"
        rules="required:claim_amount"
        v-model="data.amount"
        type="text"
        large
        :icon="euroIcon()"
        :mask="COMMA_NUMBER_MASK(10)"
    />
</template>

<script lang="ts" setup>
import { computed, watch } from "vue";
import { AsliDate, AsliRadio, AsliDropdown, AsliTextInput, getDateMinusYears } from "@mbs-npm/anpost-ui";
import { PropType, reactive } from "vue";
import { Claim } from "@/stores/risk/types/repeater/repeater-types";
import { useListStore } from "@/stores/lists/lists";
import { YOUR_COVER_HELP_TEXT } from "@/utils/helpText";
import { euroIcon } from "@/static/constants";
import { COMMA_NUMBER_MASK } from "@/static/masks";

const props = defineProps({
    claim: {
        type: Object as PropType<Claim>,
        required: true
    },
    ncdYears: {
        type: Number,
        required: true
    },
    index: {
        type: Number,
        required: true
    }
});

const emit = defineEmits<{ (e: "update", val: Claim): void }>();

const listStore = useListStore();
const lists = computed(() => listStore?.lists);
const today = new Date();

const buildId = (id: string) => `${id}_${props.index}`;

const claimDateKey = computed(() => `claim-date-${props.ncdYears}`);

const dateFrom = computed(() =>
    getDateMinusYears(props.ncdYears != null && props.ncdYears < 3 ? props.ncdYears + 1 : props.ncdYears)
);

const data = reactive<Claim>({
    type: props.claim.type,
    is_settled: props.claim.is_settled,
    date: props.claim.date,
    amount: props.claim.amount
});

const computedDateRules = computed(() => `required:claim_date|claim_free_years:${props.ncdYears}`);

watch(
    () => data,
    newData => {
        emit("update", newData);
    },
    { deep: true }
);
</script>
